// TERRA garden water — a stylized animated surface that replaces the old flat MeshStandardMaterial plane.
// Roblox's water look, done cheap: procedural ripple normals (shared noise, no bitmap), Beer-Lambert depth
// tint, cohesive shoreline detail, and a Fresnel-weighted reflection that blends REAL PLANAR reflection (a
// mirror-camera pass, since the water is one flat plane) over an analytic sky-gradient fallback. Depth +
// shoreline read the shared heightmap (heighttex.ts) — no depth prepass.
//
// GOTCHA (learned hard): a raw ShaderMaterial does NOT get tone-mapping / output color-space / fog for
// free, and the <tonemapping>/<colorspace>/<fog> chunk INCLUDES re-define the renderer's auto-injected
// functions → invisible water. So we apply ACES + linear→sRGB + linear fog MANUALLY, matching the terrain.

import * as THREE from "three";
import type { TerraField } from "./field";
import { GLSL_NOISE } from "./material";

export interface WaterHandle {
  mesh: THREE.Mesh;
  /** Per-frame: animate ripples + keep tone-map exposure and scene fog in sync with the terrain. */
  setFrame(t: number, exposure: number, fog: THREE.Fog | THREE.FogExp2 | null): void;
  /** Live-tune a scalar uniform by name (DEV panel). No-op if unknown / non-scalar. */
  setUniform(name: string, value: number): void;
}

const SK_SIZE = 72000; // matches the mesh skirt — the plane runs to the fog so the arena reads as an ocean
const CLIP_BIAS = 0.003;
const _srgb = new THREE.Color();
// planar-reflection scratch (single water plane per level → module-level reuse is safe, zero per-frame alloc)
const _plane = new THREE.Plane();
const _normal = new THREE.Vector3();
const _reflPos = new THREE.Vector3();
const _camPos = new THREE.Vector3();
const _rot = new THREE.Matrix4();
const _lookAt = new THREE.Vector3();
const _view = new THREE.Vector3();
const _target = new THREE.Vector3();
const _q = new THREE.Vector4();
const _clip = new THREE.Vector4();
const _texMat = new THREE.Matrix4();
const _dir = new THREE.Vector3();
const _size = new THREE.Vector2();
const _vcam = new THREE.PerspectiveCamera();
let _reflFrame = 0; // temporal: the planar reflection re-renders every OTHER frame
// The texture-projection matrix that PRODUCED the pixels currently in the RT. On a reuse frame the shader must
// sample with THIS, not with a matrix rebuilt from the current camera — see the note at the reuse branch.
const _reflTexMat = new THREE.Matrix4();
let _reflHaveCached = false;
// occlusion cull: an ANY_SAMPLES query on the water plane's own draw tells us if ANY water pixel is visible; if it
// was fully occluded by terrain / off-screen last frame, skip the (expensive) reflection this frame. Default VISIBLE
// so any driver hiccup just falls back to always-reflect (today's behavior).
let _occQ: WebGLQuery | null = null;      // query awaiting its result
let _occActive: WebGLQuery | null = null; // query begun this frame, not yet ended
let _occVisible = true;

export function buildWater(
  f: TerraField,
  heightTex: THREE.DataTexture,
  hMin: number,
  hMax: number,
  sun?: THREE.Vector3,
): WaterHandle {
  const geo = new THREE.PlaneGeometry(SK_SIZE * 0.9, SK_SIZE * 0.9);
  geo.rotateX(-Math.PI / 2);

  const rt = new THREE.WebGLRenderTarget(512, 512, {
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat, type: THREE.UnsignedByteType, depthBuffer: true, stencilBuffer: false,
  });
  rt.texture.colorSpace = THREE.SRGBColorSpace;

  const sunDir = (sun ? sun.clone() : new THREE.Vector3(0.4, 0.7, 0.5)).normalize();
  const uniforms = {
    uTime: { value: 0 },
    uExposure: { value: 1.0 },
    uHeight: { value: heightTex },
    uFieldMin: { value: new THREE.Vector2(f.minX, f.minZ) },
    uFieldSize: { value: new THREE.Vector2(f.maxX - f.minX, f.maxZ - f.minZ) },
    uHMin: { value: hMin },
    uHMax: { value: hMax },
    uSeaY: { value: f.seaY },
    uSunDir: { value: sunDir },
    uShallow: { value: new THREE.Color(0x2f7d84) },
    uDeep: { value: new THREE.Color(0x0a2630) },
    uSkyLo: { value: new THREE.Color(0x9fc7d6) },
    uSkyHi: { value: new THREE.Color(0x39627e) },
    uFoamColor: { value: new THREE.Color(0x5a9ba0) }, // shore detail tint — a LIGHT version of the water
    uDepthTint: { value: 0.042 },
    uFresnelPow: { value: 1.7 },
    uRippleAmp: { value: 1.0 },
    uRippleScale: { value: 0.012 },
    uRippleSpeed: { value: 0.45 },
    uFlow: { value: new THREE.Vector2(0.6, 0.42) }, // ONE coherent drift direction for BOTH octaves
    uAlphaShallow: { value: 0.88 },
    uAlphaDeep: { value: 0.6 },
    uShoreWidth: { value: 9.0 },
    uShoreFoam: { value: 1.5 },
    // planar reflection
    uReflMap: { value: rt.texture },
    uReflMatrix: { value: new THREE.Matrix4() },
    uReflStrength: { value: 0.5 }, // artist cap (DEV slider)
    uReflGate: { value: 0.0 },     // 0/1, set each frame by the reflection pass (0 = skipped)
    uDistort: { value: 0.15 },
    // scene fog (LINEAR), pushed per frame — applied in output space, like the terrain
    uFogColor: { value: new THREE.Vector3(0.62, 0.77, 0.70) },
    uFogNear: { value: new THREE.Vector2(1500, 9500) },
  };

  const mat = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    fog: false,
    side: THREE.FrontSide,
    vertexShader: /* glsl */ `
      uniform mat4 uReflMatrix;
      varying vec3 vWorldPos;
      varying float vViewZ;
      varying vec4 vReflCoord;
      void main() {
        vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        vReflCoord = uReflMatrix * vec4(position, 1.0);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vViewZ = -mv.z;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D uHeight, uReflMap;
      uniform vec2 uFieldMin, uFieldSize, uFogNear, uFlow;
      uniform float uHMin, uHMax, uSeaY, uTime, uExposure, uDepthTint, uFresnelPow, uRippleAmp, uRippleScale,
                    uRippleSpeed, uAlphaShallow, uAlphaDeep, uShoreWidth, uShoreFoam, uReflStrength, uReflGate, uDistort;
      uniform vec3 uShallow, uDeep, uSkyLo, uSkyHi, uSunDir, uFogColor, uFoamColor;
      varying vec3 vWorldPos;
      varying float vViewZ;
      varying vec4 vReflCoord;
      ${GLSL_NOISE}

      vec3 acesTonemap(vec3 c) {
        c *= uExposure / 0.6;
        const mat3 IN = mat3(0.59719, 0.07600, 0.02840, 0.35458, 0.90834, 0.13383, 0.04823, 0.01566, 0.83777);
        const mat3 OUT = mat3(1.60475, -0.10208, -0.00327, -0.53108, 1.10813, -0.07276, -0.07367, -0.00605, 1.07602);
        c = IN * c;
        vec3 a = c * (c + 0.0245786) - 0.000090537;
        vec3 b = c * (0.983729 * c + 0.432951) + 0.238081;
        c = OUT * (a / b);
        return clamp(c, 0.0, 1.0);
      }
      vec3 linearToSRGB(vec3 c) {
        return mix(pow(c, vec3(0.41666)) * 1.055 - 0.055, c * 12.92, step(c, vec3(0.0031308)));
      }
      // two octaves drifting the SAME way (one coherent flow) — no crosshatch, reads as wind-blown water
      float ripple(vec2 w) {
        vec2 flow = uFlow * (uRippleSpeed * uTime);
        return tvn(w * uRippleScale + flow)
             + tvn(w * uRippleScale * 2.25 + flow * 1.6) * 0.6;
      }

      void main() {
        vec2 w = vWorldPos.xz;

        float e = 2.0;
        float r0 = ripple(w), rx = ripple(w + vec2(e, 0.0)), rz = ripple(w + vec2(0.0, e));
        vec3 N = normalize(vec3(-(rx - r0) * uRippleAmp, 1.0, -(rz - r0) * uRippleAmp));
        vec3 V = normalize(cameraPosition - vWorldPos);

        vec2 uv = (w - uFieldMin) / uFieldSize;
        float inField = step(0.0, uv.x) * step(uv.x, 1.0) * step(0.0, uv.y) * step(uv.y, 1.0);
        float ground = mix(uHMin, uHMax, texture2D(uHeight, clamp(uv, 0.0, 1.0)).r);
        float depth = mix(9999.0, max(uSeaY - ground, 0.0), inField);

        float dtint = 1.0 - exp(-uDepthTint * depth);
        vec3 body = mix(uShallow, uDeep, dtint);

        float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), uFresnelPow);
        vec3 rdir = reflect(-V, N);
        vec3 sky = mix(uSkyLo, uSkyHi, clamp(rdir.y, 0.0, 1.0));
        sky += vec3(1.0, 0.92, 0.72) * pow(max(dot(rdir, uSunDir), 0.0), 48.0) * 0.6;
        vec3 baseLin = mix(body, sky, fres * 0.9);

        // SHORE DETAIL: driven by the SAME ripple pattern (so it moves WITH the surface), tinted only
        // toward a light version of the water color — a subtle texture change near shore, not a foam band.
        float shore = smoothstep(uShoreWidth, uShoreWidth * 0.15, depth) * inField;
        float crest = smoothstep(0.55, 1.15, r0);
        baseLin = mix(baseLin, uFoamColor, clamp(shore * crest * uShoreFoam, 0.0, 1.0));

        // output space (matches the terrain: ACES → linear→sRGB)
        vec3 col = linearToSRGB(acesTonemap(baseLin));

        // real planar reflection overlay (RT already tone-mapped + sRGB), distorted by ripples, at grazing
        vec4 rc = vReflCoord;
        rc.xy += N.xz * uDistort * rc.w;
        vec2 ruv = rc.xy / max(rc.w, 1e-4);
        float rvalid = step(0.0, ruv.x) * step(ruv.x, 1.0) * step(0.0, ruv.y) * step(ruv.y, 1.0);
        vec3 planar = texture2D(uReflMap, ruv).rgb;
        col = mix(col, planar, fres * rvalid * uReflStrength * uReflGate);

        float alpha = clamp(mix(uAlphaShallow, uAlphaDeep, dtint), 0.0, 1.0);

        float fogF = smoothstep(uFogNear.x, uFogNear.y, vViewZ);
        col = mix(col, uFogColor, fogF);
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.y = f.seaY;
  mesh.renderOrder = 1;
  mesh.frustumCulled = false;
  mesh.material.userData.rt = rt;

  // ---- planar reflection: mirror the camera across the water plane, render the scene above water into
  // the half-res RT, hand the shader the reflection texture matrix (three.js Reflector's math). ----
  const reflect = (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera): void => {
    camera.getWorldDirection(_dir);
    if (-_dir.y > 0.97 || camera.position.y < f.seaY) { uniforms.uReflGate.value = 0.0; return; }

    mesh.updateMatrixWorld();
    _reflPos.setFromMatrixPosition(mesh.matrixWorld);
    _camPos.setFromMatrixPosition(camera.matrixWorld);
    _normal.set(0, 1, 0);
    _view.subVectors(_reflPos, _camPos);
    if (_view.dot(_normal) > 0) { uniforms.uReflGate.value = 0.0; return; }
    _view.reflect(_normal).negate().add(_reflPos);

    _rot.extractRotation(camera.matrixWorld);
    _lookAt.set(0, 0, -1).applyMatrix4(_rot).add(_camPos);
    _target.subVectors(_reflPos, _lookAt).reflect(_normal).negate().add(_reflPos);

    _vcam.position.copy(_view);
    _vcam.up.set(0, 1, 0).applyMatrix4(_rot).reflect(_normal);
    _vcam.lookAt(_target);
    _vcam.far = camera.far;
    _vcam.updateMatrixWorld();
    _vcam.projectionMatrix.copy(camera.projectionMatrix);

    _texMat.set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1);
    _texMat.multiply(_vcam.projectionMatrix).multiply(_vcam.matrixWorldInverse).multiply(mesh.matrixWorld);
    uniforms.uReflMatrix.value.copy(_texMat);

    _plane.setFromNormalAndCoplanarPoint(_normal, _reflPos).applyMatrix4(_vcam.matrixWorldInverse);
    _clip.set(_plane.normal.x, _plane.normal.y, _plane.normal.z, _plane.constant);
    const pm = _vcam.projectionMatrix;
    _q.x = (Math.sign(_clip.x) + pm.elements[8]) / pm.elements[0];
    _q.y = (Math.sign(_clip.y) + pm.elements[9]) / pm.elements[5];
    _q.z = -1.0;
    _q.w = (1.0 + pm.elements[10]) / pm.elements[14];
    _clip.multiplyScalar(2.0 / _clip.dot(_q));
    pm.elements[2] = _clip.x;
    pm.elements[6] = _clip.y;
    pm.elements[10] = _clip.z + 1.0 - CLIP_BIAS;
    pm.elements[14] = _clip.w;

    renderer.getDrawingBufferSize(_size);
    const rw = Math.max(2, Math.floor(_size.x * 0.25)), rh = Math.max(2, Math.floor(_size.y * 0.25)); // quarter-res: a grazing, ripple-distorted overlay hides it
    const resized = rt.width !== rw || rt.height !== rh;
    if (resized) rt.setSize(rw, rh); // NB: setSize RE-CREATES the texture → the RT is BLACK until it's re-rendered
    uniforms.uReflGate.value = 1.0;
    // TEMPORAL: re-render every OTHER frame; odd frames reuse the RT.
    // A freshly-resized RT is black (dynamic resolution changes the buffer size → setSize → black flicker on a
    // reused frame), so whenever we just resized we MUST render this frame instead of reusing the now-black RT.
    //
    // On a REUSE frame the RT still holds the PREVIOUS render's pixels, so the projective lookup has to use the
    // matrix that PRODUCED them. The old comment here claimed "the matrix above stays fresh so it's aligned" —
    // that is backwards: fresh matrix + stale pixels is exactly what MISaligns. Uploading this frame's matrix
    // slides the whole reflection by the camera delta and snaps it back the next frame — a 30Hz wobble on water
    // during any pan, at up to 50% blend at the grazing angles you see water from on foot. Cache the matrix
    // with the pixels and reuse them as a pair.
    _reflFrame++;
    if (!resized && _reflHaveCached && (_reflFrame & 1) === 0) {
      uniforms.uReflMatrix.value.copy(_reflTexMat);
      return;
    }
    _reflTexMat.copy(_texMat);
    _reflHaveCached = true;

    const prevTarget = renderer.getRenderTarget();
    const prevShadow = renderer.shadowMap.autoUpdate;
    renderer.shadowMap.autoUpdate = false;
    mesh.visible = false;
    renderer.setRenderTarget(rt);
    renderer.clear();
    renderer.render(scene, _vcam);
    renderer.setRenderTarget(prevTarget);
    mesh.visible = true;
    renderer.shadowMap.autoUpdate = prevShadow;
  };
  mesh.onBeforeRender = (renderer, scene, camera) => {
    // ONE reflection decision per FRAME, not per pass. The water plane is also drawn in the bloom-occluder
    // re-render (black override), so this hook fires TWICE a frame — measured 2/2 in 149 of 149 frames. That
    // double-fire advanced the every-other-frame counter by two, so its parity never alternated and the
    // reflection re-rendered EVERY frame, silently costing double. The occluder pass draws the water black and
    // has no use for a reflection, so it is simply not our turn.
    if ((scene as THREE.Scene).overrideMaterial) return;
    const gl = renderer.getContext();
    const gl2 = gl instanceof WebGL2RenderingContext ? gl : null;
    // read a completed occlusion query → was the water drawing ANY pixels the last frame we measured?
    if (gl2 && _occQ && gl2.getQueryParameter(_occQ, gl2.QUERY_RESULT_AVAILABLE)) {
      _occVisible = gl2.getQueryParameter(_occQ, gl2.QUERY_RESULT) > 0;
      gl2.deleteQuery(_occQ); _occQ = null;
    }
    // reflection only when the water is actually on-screen + unoccluded (else it's invisible — don't pay for it)
    if (_occVisible && (camera as THREE.PerspectiveCamera).isPerspectiveCamera) reflect(renderer, scene as THREE.Scene, camera as THREE.PerspectiveCamera);
    else uniforms.uReflGate.value = 0.0;
    // begin an occlusion query around THIS water draw (only the plane draws between onBefore/onAfterRender; reflect()
    // already finished above, so its nested render isn't counted). ANY_SAMPLES_PASSED_CONSERVATIVE errs toward visible.
    if (gl2 && !_occQ && !_occActive) { _occActive = gl2.createQuery(); gl2.beginQuery(gl2.ANY_SAMPLES_PASSED_CONSERVATIVE, _occActive); }
  };
  mesh.onAfterRender = (renderer) => {
    const gl = renderer.getContext();
    if (gl instanceof WebGL2RenderingContext && _occActive) { gl.endQuery(gl.ANY_SAMPLES_PASSED_CONSERVATIVE); _occQ = _occActive; _occActive = null; }
  };

  return {
    mesh,
    setFrame(t: number, exposure: number, fog: THREE.Fog | THREE.FogExp2 | null): void {
      uniforms.uTime.value = t;
      uniforms.uExposure.value = exposure;
      const lf = fog as THREE.Fog | null;
      if (lf && lf.isFog) {
        (uniforms.uFogNear.value as THREE.Vector2).set(lf.near, lf.far);
        _srgb.copy(lf.color).convertLinearToSRGB();
        (uniforms.uFogColor.value as THREE.Vector3).set(_srgb.r, _srgb.g, _srgb.b);
      }
    },
    setUniform(name: string, value: number): void {
      const u = (uniforms as Record<string, { value: unknown }>)[name];
      if (u && typeof u.value === "number") u.value = value;
    },
  };
}
